/**
 * Duplicating a requirement — the pure builders (Akhil, 2026-09-16).
 *
 * A customer comes with the same list another customer ordered. Rather than triage, RFQ and
 * compare it all again, the whole thing is copied: a new enquiry with every line, and a new
 * DRAFT quotation carrying every price, margin, supplier, PO currency, discount, charge and tax
 * the source had — then adjusted on the revision page (add / remove / re-price a line). The
 * header is copied as-is; the customer and the rest are changed on the quotation's own card.
 *
 * ---- Rows are matched by POSITION ----
 *
 * `create_enquiry` numbers the rows it is given (`toNewItemWireRow` sends no `enquiry_row_no`),
 * so the new enquiry's serials are its own. Lines are sent in the source's serial order and the
 * new rows read back in that order, so the i-th source line IS the i-th new row; that is how the
 * quotation lines find their `enquiry_row_no` (the through-key a quotation line must carry —
 * see `QuotationLinePayload`) and how route and contract rate reach the new lines. A count that
 * disagrees is refused rather than priced blindly.
 *
 * ---- "Everything" means the WIRE row ----
 *
 * The quotation items are copied from the source's `get_quotation` rows as objects, not from the
 * mapped `QuotationLine`: the domain type deliberately does not model `supplier`,
 * `supplier_quotation`, `lowest_quote_supplier` or `supplier_note` (D22), and those are exactly
 * the provenance a duplicate must keep. Every known payload key that is present is copied; the
 * row's own identity (`name`, `idx`, `parent`…) is not.
 */

import { withoutInternalAnnotations } from './internalNotes'
import type { NewEnquiryHeader } from './api/useEnquiryMutations'
import type { QuotationTerms } from './api/useQuotation'
import type { CreateQuotationPayload, QuotationLinePayload } from './quotationFromAllocation'
import type { Enquiry, EnquiryItem, NewEnquiryItem } from './types'

const bySerial = (a: EnquiryItem, b: EnquiryItem) => Number(a.cust_sno) - Number(b.cust_sno) || a.cust_sno.localeCompare(b.cust_sno)

/** The `create_enquiry` header for the copy — the source's own, minus what belongs to that order. */
export function duplicateEnquiryHeader(source: Enquiry): NewEnquiryHeader {
  const h = source.header
  const items: NewEnquiryItem[] = [...source.items].sort(bySerial).map((line) => ({
    item_code: line.item_code,
    qty: line.qty,
    uom: line.uom,
    ...(line.description && withoutInternalAnnotations(line.description) ? { description: withoutInternalAnnotations(line.description) } : {}),
    ...(line.raw_text ? { raw_text: line.raw_text } : {}),
    ...(line.customer_uom ? { customer_uom: line.customer_uom } : {}),
    suppliers: line.suppliers,
  }))
  return {
    source: 'manual',
    customer: h.customer,
    ...(h.company ? { company: h.company } : {}),
    enquiry_type: h.enquiry_type,
    vessel: h.vessel ?? null,
    delivery_location: h.delivery_location ?? null,
    delivery_port: h.delivery_port ?? null,
    ...(h.purchaser ? { purchaser: h.purchaser } : {}),
    ...(h.sales_person ? { sales_person: h.sales_person } : {}),
    items,
  }
}

/**
 * What `create_enquiry` cannot take per line — route and the contract rate — as patches on the
 * new rows, matched by position. Only rows that differ are patched.
 */
export function pricingPatchesByPosition(
  sourceLines: readonly EnquiryItem[],
  createdLines: readonly EnquiryItem[],
): Array<{ lineId: string; patch: { route: 'contract' | 'rfq'; selected_rate: number } }> {
  const src = [...sourceLines].sort(bySerial)
  const dst = [...createdLines].sort(bySerial)
  if (src.length !== dst.length) return []
  const out: Array<{ lineId: string; patch: { route: 'contract' | 'rfq'; selected_rate: number } }> = []
  src.forEach((line, i) => {
    const target = dst[i]
    const route = line.route === 'contract' ? 'contract' : 'rfq'
    const rate = Number(line.selected_rate) || 0
    if (target.route === route && (Number(target.selected_rate) || 0) === rate) return
    out.push({ lineId: target.line_id, patch: { route, selected_rate: rate } })
  })
  return out
}

/** A source quotation as `get_quotation` returns it — read loosely, so every present field copies. */
export type WireQuotationDoc = Record<string, unknown> & { items?: Array<Record<string, unknown>> }

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0)
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))
const round2 = (n: number) => Math.round(n * 100) / 100

/** Every `QuotationLinePayload` key copied when the row has it, by kind. */
const LINE_STRINGS = ['item_code', 'item_name', 'description', 'source', 'uom', 'sell_uom', 'lowest_quote_supplier', 'offer_remarks', 'supplier', 'po_currency', 'supplier_quotation', 'supplier_note'] as const
const LINE_NUMBERS = ['qty', 'cost_unit', 'default_margin_pct', 'margin', 'sell_unit', 'unit_price', 'amount', 'selected_rate', 'lowest_quote_price', 'po_currency_rate', 'conversion_rate'] as const

function duplicateLine(row: Record<string, unknown>, enquiryRowNo: string): QuotationLinePayload {
  const out: Record<string, unknown> = { enquiry_row_no: enquiryRowNo }
  for (const key of LINE_STRINGS) if (row[key] != null && row[key] !== '') out[key] = str(row[key])
  // A quotation made before 2026-10-02 may carry our notes; the copy never does.
  if (typeof out.description === 'string') out.description = withoutInternalAnnotations(out.description)
  for (const key of LINE_NUMBERS) if (row[key] != null) out[key] = num(row[key])
  // The payload's required keys, defaulted from what the row does carry when a column is absent.
  const sell = num(out.sell_unit ?? row.rate ?? 0)
  const qty = num(out.qty ?? 0)
  out.item_code = str(out.item_code)
  out.item_name = str(out.item_name ?? out.item_code)
  out.source = str(out.source ?? 'Supplier')
  out.qty = qty
  out.uom = str(out.uom)
  out.sell_uom = str(out.sell_uom ?? out.uom)
  out.cost_unit = num(out.cost_unit ?? 0)
  out.default_margin_pct = num(out.default_margin_pct ?? out.margin ?? 0)
  out.margin = num(out.margin ?? out.default_margin_pct ?? 0)
  out.sell_unit = sell
  out.unit_price = num(out.unit_price ?? sell)
  out.amount = num(out.amount ?? round2(sell * qty))
  out.selected_rate = num(out.selected_rate ?? out.cost_unit)
  out.lowest_quote_price = num(out.lowest_quote_price ?? 0)
  out.lowest_quote_supplier = str(out.lowest_quote_supplier ?? '')
  return out as unknown as QuotationLinePayload
}

/** The `create_quotation` body for the copy: a fresh draft on the new enquiry, lines as the source's. */
export function duplicateQuotationPayload(source: WireQuotationDoc, created: Enquiry, transactionDate: string): CreateQuotationPayload {
  const rows = [...(source.items ?? [])].sort((a, b) => Number(a.enquiry_row_no) - Number(b.enquiry_row_no))
  const targets = [...created.items].sort(bySerial)
  if (rows.length !== targets.length) {
    throw new Error(`The copy has ${targets.length} lines but the quotation being copied has ${rows.length} — not priced.`)
  }
  const items = rows.map((row, i) => duplicateLine(row, targets[i].cust_sno))
  const netTotal = round2(items.reduce((sum, i) => sum + i.amount, 0))
  return {
    status: 'Draft',
    revision: 0,
    enquiry: created.id,
    customer: str(source.customer),
    customer_name: str(source.customer_name) || str(source.customer),
    transaction_date: transactionDate,
    customer_currency: str(source.customer_currency),
    exchange_rate: num(source.exchange_rate) || 1,
    // The comparison belonged to the source enquiry; the copy has none.
    supplier_quote_comparison: '',
    sales_person: str(source.sales_person),
    purchaser: str(source.purchaser),
    vessel: str(source.vessel),
    delivery_location: str(source.delivery_location),
    ...(str(source.port) ? { port: str(source.port) } : {}),
    // Zero for the same reason `quotationFromAllocation` sends zero: the lines carry the margin.
    markup: 0,
    net_total: netTotal,
    total: netTotal,
    grand_total: netTotal,
    items,
  }
}

const TERM_NUMBERS = ['markup', 'line_markup', 'line_commission', 'management_commission', 'convenience_amount', 'software_commission', 'port_agent_commission', 'other_charges', 'additional_discount_percentage', 'additional_discount_amount'] as const
const TERM_STRINGS = ['tc_name', 'terms', 'payment_terms_template', 'customer_address'] as const

/** The document-level terms to write after creation — each only when the source has it. */
export function duplicateQuotationTerms(source: WireQuotationDoc): QuotationTerms {
  const out: Record<string, unknown> = {}
  for (const key of TERM_NUMBERS) if (source[key] != null) out[key] = num(source[key])
  for (const key of TERM_STRINGS) if (source[key] != null && source[key] !== '') out[key] = str(source[key])
  const applyOn = str(source.apply_discount_on)
  if (applyOn === 'Net Total' || applyOn === 'Grand Total') out.apply_discount_on = applyOn
  const taxes = (Array.isArray(source.taxes) ? (source.taxes as Array<Record<string, unknown>>) : []).map((t, i) => ({
    charge_type: str(t.charge_type),
    account_head: str(t.account_head),
    tax_rate: num(t.tax_rate),
    tax_amount: num(t.tax_amount),
    row_id: str(t.row_id) || String(i + 1),
  }))
  out.taxes = taxes
  return out as QuotationTerms
}
